/**
 * Service-token header for the `/secrets/infisical` endpoints.
 *
 * Those routes sit behind a service-token guard on the Nest side and reject a plain user
 * session with `403 { message: "Service token required" }`, so they need the service token
 * *in addition to* the `Authorization: Bearer <session>` header `getAuthHeaders` supplies.
 *
 * Verified against the live backend: the token goes in `Authorization: Bearer <token>` and
 * *replaces* the user session token rather than sitting beside it. Sending the session token
 * there is exactly what produces `403 { message: "Service token required" }` — the guard
 * finds a user JWT where it wants the service JWT. So these headers must be spread *after*
 * `getAuthHeaders()`, and `Authorization` is capitalised to match its key so it overrides
 * cleanly instead of adding a second, differently-cased header.
 *
 *   REACT_APP_SECRETS_SERVICE_TOKEN         — the token itself
 *   REACT_APP_SECRETS_SERVICE_TOKEN_HEADER  — header name, defaults to `Authorization`
 *
 * The header name stays configurable in case other service-guarded routes read a custom
 * header (`x-service-token`, `x-api-key`, …); `Authorization` gets the `Bearer ` prefix,
 * anything else gets the bare token.
 *
 * NOTE: CRA inlines `REACT_APP_*` into the JS bundle, so this token ships to every browser
 * that loads the panel. Scope it as narrowly as the backend allows.
 */

const TOKEN_ENV = "REACT_APP_SECRETS_SERVICE_TOKEN";
const HEADER_ENV = "REACT_APP_SECRETS_SERVICE_TOKEN_HEADER";
const DEFAULT_HEADER = "Authorization";

/** The configured service token, trimmed. Empty string when unset. */
export function getServiceToken() {
  const raw = process.env[TOKEN_ENV];
  return typeof raw === "string" ? raw.trim() : "";
}

/** True when a service token is configured. */
export function hasServiceToken() {
  return getServiceToken() !== "";
}

/**
 * Header name the guard reads. `authorization` in any casing normalises to `Authorization`
 * so that spreading these headers overrides `getAuthHeaders()` instead of producing both
 * `Authorization` and `authorization` — which would leave the session token in play and keep
 * the 403 coming.
 */
export function getServiceTokenHeaderName() {
  const raw = process.env[HEADER_ENV];
  const name = typeof raw === "string" ? raw.trim() : "";
  if (!name) return DEFAULT_HEADER;
  return name.toLowerCase() === "authorization" ? DEFAULT_HEADER : name.toLowerCase();
}

/**
 * `{ [header]: token }`, or `{}` when no token is configured — spread *after*
 * `getAuthHeaders()` so an unset token degrades to session-only auth instead of sending an
 * empty header.
 */
export function getServiceTokenHeaders() {
  const token = getServiceToken();
  if (!token) return {};

  const header = getServiceTokenHeaderName();
  return { [header]: header === DEFAULT_HEADER ? `Bearer ${token}` : token };
}
